import c3 from "c3";
import d3 from "d3";
import moment from "moment";

import { ObservationDTO } from "@/Common/Api/Labs/Dto/Responses/LabMessageGroupDTO";
import { ArrayHelper } from "@/Common/Helpers/ArrayHelper";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";

interface ILabGraphProperties {
    StartDate: Date;
    EndDate: Date;
    Observations: ObservationDTO[];
    MasterObservation: ObservationDTO;
    ObservationTypeIdentifier: string;
    HeaderText: string;
}

class LabGraph extends React.Component<ILabGraphProperties, never> {
    private chart: c3.ChartAPI;
    private filteredObservations: ObservationDTO[];
    private timeoutReferenceDebouncedGraphRender: number;

    constructor(props: ILabGraphProperties) {
        super(props);
        this.initGraphData();
    }

    public componentDidUpdate(): void {
        this.initGraphData();
        this.debouncedRenderChart();
    }

    public componentDidMount(): void {
        this.debouncedRenderChart();
    }

    public componentWillUnmount(): void {
        this.tryDestroyChart();
    }

    public tryDestroyChart(): void {
        try {
            this.chart && this.chart.destroy();
        }
        catch { }
    }

    public initGraphData(): void {
        this.filteredObservations = this.getFilteredObservations();
    }

    public getFilteredObservations(): ObservationDTO[] {
        return this.props.Observations
            ?.filter(x => !this.props.StartDate || moment(x.Date).toDate() >= this.props.StartDate)
            .filter(x => !this.props.EndDate || moment(x.Date).toDate() <= this.props.EndDate)
            .filter(x => !isNaN(this.toFloat(x.ValueText)))
        || [];
    }

    public render(): JSX.Element {
        const observationsToRender = this.getFilteredObservations();

        return (
            <div>
                <div className="tb lab-component-header">
                    <div className="tbc left">
                        <h3 className="thin-font no-margin" style={{ paddingTop: "15px" }}>
                            {this.props.MasterObservation.Text}
                        </h3>
                    </div>
                </div>
                <div className="tb lab-component">
                    <div className="tbc left">
                        <div className="fixed-hd-tb">
                            <div className="sch-head-tb">
                                <table className="main">
                                    {this.getColumnWidth()}
                                    <thead>
                                        <tr>
                                            <th>{cacheStore.GetString("DateLabel")}</th>
                                            <th>{cacheStore.GetString("ValueLabel")}</th>
                                            <th>{cacheStore.GetString("SourceLabel")}</th>
                                        </tr>
                                    </thead>
                                </table>
                            </div>
                            <div className="patch-fix" />
                            <div className="lab-details-hl">
                                <table className="main">
                                    {this.getColumnWidth()}
                                    <tbody>
                                        {observationsToRender.sortByPropertyDesc(x => x.Date).map((x, idx) => (
                                            <tr key={this.props.ObservationTypeIdentifier + idx}>
                                                <td>{MyleHelper.ReplaceEmptyString(x.Date ? moment(x.Date).format("YYYY-MM-DD HH:mm:ss") : "")}</td>
                                                <td className="wrap-text" title={x.UnitsIdentifier && x.UnitsIdentifier}>
                                                    {MyleHelper.ReplaceEmptyString(this.formatObservationValue(x))}
                                                </td>
                                                <td>{MyleHelper.ReplaceEmptyString(x.Provider)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                    <div className="tbc right">
                        <div>
                            <div id={"chart" + this.props.ObservationTypeIdentifier} />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    public debouncedRenderChart(): void {
        clearTimeout(this.timeoutReferenceDebouncedGraphRender);
        this.timeoutReferenceDebouncedGraphRender = window.setTimeout(() => this.renderGraph(), 250);
    }

    private renderGraph(): void {
        const filteredDates = this.filteredObservations.filter(x => !IsNullOrUndefined(x.Date)) || [];
        const dates: any[] = filteredDates.map(x => moment(x.Date).toDate());
        const values: any[] = filteredDates.map(x => this.toFloat(x.ValueText));

        this.tryDestroyChart();

        if (values && values.length > 0) {
            // calculations
            const referenceRange: [number, number] = this.getReferenceRange(this.props.MasterObservation.ReferenceRange);
            const yAxisRange: [number, number] = this.getYAxisRange(referenceRange, values);
            const maxDecimalRange = values.map(x => this.countDecimalPlaces(x)).sortByPropertyDesc(x => x).firstOrDefault() || 0;

            // customize chart display
            const xmin = (this.props.StartDate || dates.sortByProperty(x => x).firstOrDefault());
            const xmax = (this.props.EndDate || dates.sortByPropertyDesc(x => x).firstOrDefault());

            const ymin = yAxisRange[0];
            const ymax = yAxisRange[1];
            const valueFormat = maxDecimalRange > 0 ? d3.format("." + maxDecimalRange + "f") : d3.format("g");

            var refLinesObj = [];
            if (referenceRange) {
                refLinesObj.push({ value: referenceRange[0], class: "red", text: "ref. min" });
                refLinesObj.push({ value: referenceRange[1], class: "red", text: "ref. max" });
            }

            var uniqueDates = ArrayHelper.Unique(dates.slice());
            var labelCount = uniqueDates.length;

            this.chart = c3.generate({
                size: { height: 450 },
                bindto: "#chart" + this.props.ObservationTypeIdentifier,
                data: {
                    x: "dates",
                    columns: [["dates"].concat(dates), [this.props.MasterObservation.Text].concat(values)],
                    color: (color, d) => {
                        if (d == this.props.MasterObservation.Text)
                            return "#4A95D1";
                        else if (typeof (d) == "object") {
                            if (d.value && referenceRange) {
                                if (d.value < referenceRange[0] || d.value > referenceRange[1])
                                    return "red";
                            }
                        }
                        return "#4A95D1";
                    }
                },
                axis: {
                    x: {
                        min: xmin,
                        max: xmax,
                        type: "timeseries",
                        tick: {
                            count: labelCount,
                            format: "%Y-%m-%d"
                        }
                    },
                    y: {
                        max: ymax,
                        min: ymin,
                        padding: { top: 0, bottom: 0 },
                        tick: {
                            format: valueFormat // set 2 decimals precision on y-axis labels
                        }
                    }
                },
                grid: {
                    x: { show: true },
                    y: { show: true, lines: refLinesObj }
                },
                padding: { right: 50 }
            });
        }
    }

    private formatObservationValue(obs: ObservationDTO): string {
        const num = this.toFloat(obs.ValueText);

        if (!isNaN(num) && this.props.MasterObservation.UnitsIdentifier)
            return `${num.toString()} ${this.props.MasterObservation.UnitsIdentifier}`;
        else if (!isNaN(num))
            return num.toString();
        else
            return obs.ValueText;
    }

    private toFloat(value: string): number {
        if (IsNullOrUndefined(value))
            return NaN;

        const tmpValue = value.replace(",", ".").replaceAll("<", "").replaceAll(">", "");
        return parseFloat(tmpValue);
    }

    private getColumnWidth(): JSX.Element {
        return (
            <colgroup>
                <col style={{ width: "45%" }} />
                <col style={{ width: "35%" }} />
                <col style={{ width: "20%" }} />
            </colgroup>
        );
    }

    private getReferenceRange(refString: string): [number, number] {
        if (refString && refString.length > 0) {
            const internvalSplit = refString.split(/[^0-9,.]/).filter(x => x && x.length > 0 && !Number.isNaN(parseFloat(x))).map(x => parseFloat(x));
            if (internvalSplit.length > 1) {
                if (!Number.isNaN(internvalSplit[0]) && !Number.isNaN(internvalSplit[1]))
                    return [internvalSplit[0], internvalSplit[1]];
            }

            const isLowerSplit = refString.split(/[<]/);
            if (isLowerSplit.length == 2) {
                let maxValue = parseFloat(isLowerSplit[1]);
                if (!Number.isNaN(maxValue))
                    return [0, parseFloat(isLowerSplit[1])];
            }
        }

        return null;
    }

    private getYAxisRange(referenceRange: [number, number], values: number[]): [number, number] {
        if (values && values.length > 0) {
            // add reference values to the game
            if (referenceRange) {
                values.push(referenceRange[0]);
                values.push(referenceRange[1]);
            }

            values = values.filter(x => typeof (x) === "number"); // remove non string number

            if (values.length > 0) {
                var maxValue = Math.max.apply(Math, values);
                var minValue = Math.min.apply(Math, values);

                var valueRange = maxValue - minValue;
                var padding = valueRange * .20;
                var min = minValue - padding;
                var max = maxValue + padding;

                return [min, max];
            }
        }
        return null;
    }

    private countDecimalPlaces(num: number): number {
        const match = ("" + num).match(/(?:\.(\d+))?(?:[eE]([+-]?\d+))?$/);
        if (!match)
            return 0;

        return Math.max(
            0,
            (match[1] ? match[1].length : 0) // Number of digits right of decimal point.
            - (match[2] ? +match[2] : 0) // Adjust for scientific notation.
        );
    }
}

export { LabGraph };
